import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { GasInfluxBase } from '../../models/internal-pressure-profile.model';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { PoreFrac } from 'src/app/perivis/models/poreFrac.model';
import { UntypedFormControl } from '@angular/forms';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models/well-component';
import { InfluxFracDepthBaseComponent } from './influx-frac-depth-base/influx-frac-depth-base.component';
import { lastValueFrom } from 'rxjs';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';

interface GasInfluxBaseForm {
  limitToFracInOpenHole: boolean;
  fractureDepth: UomQuantityInput;
  fractureGradient: UomQuantityInput;
  fractureMarginOfError: UomQuantityInput;
  porePressure: UomQuantityInput;
  influxDepth: UomQuantityInput;
  useGasGravity: boolean;
  gasGradient: UomQuantityInput;
  gasGravity: UomQuantityInput;
  mudGasInterface: UomQuantityInput;
  fluidDensity: UomQuantityInput;
}

@Component({
  selector: 'app-gas-influx-base',
  templateUrl: './gas-influx-base.component.html',
  providers: subformComponentProviders(GasInfluxBaseComponent),
  standalone: false
})
export class GasInfluxBaseComponent implements AfterViewInit {
  @Input() userUnits: UserUnitsModel;
  @Input() currentString: any;
  @Input() fracPressures: PoreFrac[];
  @Input() porePressures: PoreFrac[];
  @Input() influxDepthRange: { min: number, max: number };
  @Input() fractureDepthDefault: number;
  @Input() nextStringMudWeight: number;
  @Input() tubulars: WellComponent[];
  @Input() isGasOverMud: boolean;
  @Input() wellheadDepth: number;

  @ViewChild(InfluxFracDepthBaseComponent) influxFracDepthBase!: InfluxFracDepthBaseComponent;

  public limitToFracInOpenHoleDisabled: boolean;
  public percentLabel: string;

  constructor(private _trajectoryService: TrajectoryService) {}

  public form = createForm<GasInfluxBase, GasInfluxBaseForm>(this, {
    formType: FormType.SUB,
    formControls: {
      limitToFracInOpenHole: new UntypedFormControl(false),
      fractureDepth: new UntypedFormControl(null),
      fractureGradient: new UntypedFormControl(null),
      fractureMarginOfError: new UntypedFormControl(null),
      porePressure: new UntypedFormControl(null),
      influxDepth: new UntypedFormControl(null),
      useGasGravity: new UntypedFormControl(false),
      gasGradient: new UntypedFormControl(null),
      gasGravity: new UntypedFormControl(null),
      mudGasInterface: new UntypedFormControl(null),
      fluidDensity: new UntypedFormControl(null)
    },
    toFormGroup: (obj: GasInfluxBase): GasInfluxBaseForm => {
      if (!obj) {
        return null;
      }

      this.limitToFracInOpenHoleDisabled = !obj.limitToFracInOpenHole;

      return {
        limitToFracInOpenHole: obj.limitToFracInOpenHole,
        fractureDepth: { control: obj.fractureDepth },
        fractureGradient: { control: obj.fractureGradient },
        fractureMarginOfError: { control: obj.fractureMarginOfError },
        porePressure: { control: obj.porePressure },
        influxDepth: { control: obj.influxDepth },
        useGasGravity: obj.useGasGravity,
        gasGradient: { control: obj.gasGradient },
        gasGravity: { control: obj.gasGravity },
        mudGasInterface: { control: obj.mudGasInterface },
        fluidDensity: { control: obj.fluidDensity },
      }
    },
    fromFormGroup: (formValue: GasInfluxBaseForm): GasInfluxBase | null => {
      const { limitToFracInOpenHole, fractureDepth, fractureGradient, fractureMarginOfError, porePressure, influxDepth, useGasGravity, gasGradient, gasGravity, mudGasInterface, fluidDensity, ...commonValues } = formValue;

      this.limitToFracInOpenHoleDisabled = !limitToFracInOpenHole;

      return {
        limitToFracInOpenHole: limitToFracInOpenHole,
        fractureDepth: +fractureDepth?.control,
        fractureGradient: +fractureGradient?.control,
        fractureMarginOfError: +fractureMarginOfError?.control,
        porePressure: +porePressure?.control,
        influxDepth: +influxDepth?.control,
        useGasGravity: useGasGravity,
        gasGradient: +gasGradient?.control,
        gasGravity: +gasGravity?.control,
        mudGasInterface: +mudGasInterface?.control,
        fluidDensity: +fluidDensity?.control,
        ...commonValues
      }
    }
  });

  ngAfterViewInit(): void {
    this.influxFracDepthBase.getEmwDefaultFrac(this.influxDepthRange.max, this.fractureDepthDefault, false, false);
    this.influxFracDepthBase.gasGravityChange();
    setTimeout(() => {
      this.getPercent();
    }, 50);
  }

  public limitToFracChange() {
    this.limitToFracInOpenHoleDisabled = !this.limitToFracInOpenHoleDisabled;
    this.influxFracDepthBase.getEmwDefaultFrac(this.influxDepthRange.max, this.fractureDepthDefault, false, false);
  }

  public async getPercent(): Promise<void> {
    const influxDepth = this.form.formGroup.get(this.form.formControlNames.influxDepth)?.value?.control;
    const mudGasInterface = this.form.formGroup.get(this.form.formControlNames.mudGasInterface)?.value?.control;

    if (influxDepth != null && mudGasInterface != null && mudGasInterface !== 0) {
      const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([influxDepth, mudGasInterface]));
      const percent = (tvds[1] / tvds[0]) * 100;
      const labelBase = this.isGasOverMud ? 'Gas Percent:' : 'Mud Percent:';
      this.percentLabel = labelBase + ' ' + percent.toFixed(2) + '%' + ' (TVD)';
    } else {
      this.percentLabel = 'N/A';
    }
  }
}
